// =============================================================================
// domain/patternBoard.ts — the Pattern Playbook kanban and its cards.
// -----------------------------------------------------------------------------
// `SAMPLE 27  WIN 59%  EXP -$9` on every card, the six-cell strip at the top of
// the Edit Pattern modal, and the grouping into category columns.
//
// ── 🚨 THIS POPULATION IS NOT SETUP QUALITY'S, AND THE DIFFERENCE IS REAL ──
//
// Beta computes the SAME patterns two different ways on two different screens:
//
//   Pattern Playbook   patternStats()          t.core && t.annotated !== false
//   Setup Quality      renderPatternLeaderboard  tradesForActiveAccounts()
//                                                 .filter(countsForMetrics)
//
// So a pattern can legitimately read `SAMPLE 27` here and a different sample on
// the Setup Quality leaderboard, because:
//
//   • the Playbook has NO account selection and NO `countsForMetrics` — SIM and
//     market-replay trades are counted
//   • the leaderboard has no annotation filter — unreviewed trades are counted
//
// Neither is a superset of the other. This looks like a bug on a side-by-side and
// is not one; it is what Josh's numbers mean today, and D77 is explicit that
// "improving" a ported population is out of scope. The question of whether the
// Playbook SHOULD count replay is the same one already open on the Exit Model
// Catalog, and it is his to answer.
//
// ⚠ `t.core` has no equivalent here and is deliberately not modelled. It is beta's
// guard against a trade with no core block, which its own comment explains is
// there so `valued === trade count` for USD. The flattened schema has typed
// columns instead — `net_pnl` is a column, not a nested object — so there is no
// state this gate could exclude. It is absent because it cannot fire, not because
// it was missed.
//
// ── LAST 30D TAKES ITS BOUNDARY AS A PARAMETER ──
//
// `domain/` cannot reach a clock. Beta's cutoff is `new Date()` minus 30 days —
// a timestamp carrying today's TIME of day — compared against each trade's date
// at local midnight, so the cutoff DATE ITSELF is excluded. `after` reproduces
// that exactly with a string compare (and differs only if beta is run at exactly
// 00:00:00.000, which is not a case worth carrying a Date for).
// =============================================================================

import {
  computeLeaderboardStats, type LeaderboardStats, type UnitValue,
} from './patternLeaderboard';
import { patternCategoryOf, type PatternCategory } from './patternCategories';

export interface PatternBoardTrade {
  patternId?: string | null;
  netPnl?: number | null;
  rrRealized?: number | null;
  /**
   * 🚨 CARRIED FOR THE VALUE CALLBACK, and their absence was a silent zero.
   *
   * The caller narrows each row to this shape, and when the board started
   * summing in the display unit the callback got an object with no `points`
   * and no `contract` — so every pattern reported an expectancy of 0 pts while
   * the dollar view was correct. Plausible, uniform, and invisible to every
   * test that runs in dollars.
   */
  points?: number | null;
  contract?: string | null;
  /** The day-boundary-resolved date — the caller applies the mode. */
  effectiveDate?: string | null;
}

/** What the board needs from a pattern. The full record carries far more. */
export interface BoardPattern {
  id: string;
  name: string;
  description: string;
  category: string;
  status: string;
}

export interface PatternStats extends LeaderboardStats {
  /** Trades in the last-30-day window. */
  last30Trades: number;
  /** Their summed unit value. */
  last30Pnl: number;
}

export interface PatternBoardRow {
  id: string;
  pattern: BoardPattern;
  stats: PatternStats;
}

/**
 * Stats for ONE pattern's trades.
 *
 * ⚠ Reuses `computeLeaderboardStats` rather than recomputing, for the reason the
 * exit-model board gives: wins classify on the SIGN of net P&L and scratches stay
 * out of the win-rate denominator, and a second implementation is a second chance
 * to get either wrong on a screen the user reads beside the first.
 *
 * ⚠ `last30Pnl` sums `unitValue ?? 0` — beta's `(tradeValue(t, unit) ?? 0)` — so a
 * trade with no value in the display unit contributes zero HERE while being
 * skipped entirely from `expectancy`. Inconsistent, and matched deliberately: in
 * USD every trade has a value, so the two agree on the only unit shipped so far,
 * and changing it would move a number Josh already reads.
 */
export function patternStats(
  trades: readonly PatternBoardTrade[],
  unitValue: UnitValue,
  after: string | null,
): PatternStats {
  const base = computeLeaderboardStats(trades, unitValue);
  const recent = after
    ? trades.filter((t) => (t.effectiveDate ?? '') > after)
    : trades;
  return {
    ...base,
    last30Trades: recent.length,
    last30Pnl: recent.reduce((s, t) => s + (unitValue(t) ?? 0), 0),
  };
}

/**
 * One row per pattern IN THE CATALOGUE, including patterns with no trades.
 *
 * ⚠ CATALOGUE-DRIVEN, like the exit-model board. A pattern the user wrote but has
 * never tagged still occupies a card — Josh has three of them (his candidates, all
 * at `SAMPLE 0`), and they are the ones with a Promote button. Deriving the
 * columns from the TRADES instead would hide exactly the patterns that need an
 * action.
 */
export function patternBoard(
  patterns: readonly BoardPattern[],
  trades: readonly PatternBoardTrade[],
  unitValue: UnitValue,
  after: string | null,
): PatternBoardRow[] {
  const byPattern = new Map<string, PatternBoardTrade[]>();
  for (const t of trades) {
    const id = t.patternId ? String(t.patternId) : '';
    if (!id) continue;
    const bucket = byPattern.get(id);
    if (bucket) bucket.push(t);
    else byPattern.set(id, [t]);
  }

  return patterns.map((p) => ({
    id: p.id,
    // ── 🚨 PROJECTED FIELD BY FIELD, NOT `pattern: p` ──
    //
    // The callers hand in a full `PatternSummary` — fourteen more fields, `extras`
    // among them. Passing the object straight through would make `row.pattern` the
    // whole record AT RUNTIME while typed as five fields, so
    // `row.pattern as PatternSummary` in the UI would type-check, work, and be
    // undetectable — right up until someone maps the rows and the editor opens on
    // a record with no criteria and saves that over the real one (D62).
    //
    // ⚠ A REBUILD, NOT A SPREAD, AND THAT IS THE POINT. This costs five lines and
    // buys a guarantee the type system alone cannot give: the cast now genuinely
    // loses the data, so the test that opens the editor and looks for a stored
    // criterion FAILS. It was written before this change and passed anyway —
    // an assertion that was true of a case that could not expose the defect,
    // which is the fourth time that shape has appeared in this port.
    pattern: {
      id: p.id,
      name: p.name,
      description: p.description,
      category: p.category,
      status: p.status,
    },
    stats: patternStats(byPattern.get(p.id) ?? [], unitValue, after),
  }));
}

export type PatternSortKey = 'sample' | 'winRate' | 'expectancy' | 'totalPnl' | 'name';

/**
 * Beta's five sorters. Sample size descending is the default, and the same
 * argument applies as on the exit-model board: a setup with three trades behind it
 * should not head a list that is read as evidence.
 *
 * ⚠ The name tiebreak is NOT in beta — its sorters are bare subtractions, so eleven
 * patterns at `SAMPLE 0` come out in whatever order the array happened to hold and
 * can reshuffle between renders. Added here because the columns are re-sorted on
 * every keystroke in the editor above them, and a grid that reorders under the
 * cursor reads as a bug. It cannot change which patterns appear, only the order of
 * exact ties.
 */
export function sortPatterns(
  rows: readonly PatternBoardRow[], key: PatternSortKey,
): PatternBoardRow[] {
  const out = [...rows];
  out.sort((a, b) => {
    if (key === 'name') return a.pattern.name.localeCompare(b.pattern.name);
    const av = a.stats[key];
    const bv = b.stats[key];
    if (av !== bv) return bv - av;
    return a.pattern.name.localeCompare(b.pattern.name);
  });
  return out;
}

export interface PatternColumn {
  name: string;
  color: string;
  rows: PatternBoardRow[];
}

export interface PatternGrid {
  columns: PatternColumn[];
  /**
   * Patterns whose stored category matches no current one.
   *
   * ⚠ SURFACED IN A BANNER, NOT IN A COLUMN. Beta is explicit about this — they
   * "don't occupy any of the four columns" — and the banner is a call to action
   * with a click-through to each one, because an uncategorized pattern is invisible
   * on a screen whose whole structure is categories.
   */
  uncategorized: PatternBoardRow[];
}

/**
 * Deal the sorted rows into category columns.
 *
 * ⚠ Column order is the CATEGORY ARRAY's order, and row order within a column is
 * the incoming sort order. Both are load-bearing: the user arranges the columns in
 * settings, and the sort select above the grid drives the cards.
 */
export function patternGrid(
  rows: readonly PatternBoardRow[], cats: readonly PatternCategory[],
): PatternGrid {
  const columns = cats.map((c) => ({
    name: c.name,
    color: c.color,
    rows: rows.filter((r) => patternCategoryOf(cats, r.pattern.category) === c.name),
  }));
  return {
    columns,
    uncategorized: rows.filter((r) => !patternCategoryOf(cats, r.pattern.category)),
  };
}
